// src/components/b2bClient/BulkUploadB2BTest.jsx

import React, { useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Button, message, Upload, Tooltip, Form, Select, Modal } from "antd";
import * as XLSX from "xlsx";
import {
    create_bulk_b2b_tests,
    create_bulk_b2b_packages,
} from "../../endpoints/API";

const { Dragger } = Upload;

const BulkUploadB2BTest = ({
    B2BClients,
    isBulkUploadModalOpen,
    setIsBulkUploadModalOpen,
    onSuccess,
}) => {
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [b2bClient, setB2bClient] = useState(null);
    const [uploadType, setUploadType] = useState("test"); // default: test

    const props = {
        name: "file",
        multiple: false,
        beforeUpload(file) {
            const isExcel =
                file.type ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                file.type === "application/vnd.ms-excel";

            if (!isExcel) {
                message.error("You can only upload Excel files!");
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false;
        },
        onRemove() {
            setFileList([]);
        },
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.error("No file selected!");
            return;
        }
        if (!b2bClient) {
            message.error("Select a B2B Client!");
            return;
        }

        setLoading(true);
        try {
            const file = fileList[0];
            const reader = new FileReader();

            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];

                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                const [columns, ...rows] = jsonData;

                const extractedData = rows?.map((row) => {
                    const rowData = {};
                    columns.forEach((col, index) => {
                        rowData[col] = row[index] || null;
                    });
                    return rowData;
                });

                let formattedData =
                    uploadType === "test"
                        ? extractedData.map((item) => ({
                            test_name: item?.test_name || item?.TestName || "",
                            b2b_price: item?.b2b_price || item?.B2BAmount || "",
                        }))
                        : extractedData.map((item) => ({
                            package_name: item?.package_name || item?.PackageName || "",
                            b2b_price: item?.b2b_price || item?.B2BAmount || "",
                        }));

                // 🚀 Filter out empty rows
                formattedData = formattedData.filter(
                    (item) =>
                    (uploadType === "test"
                        ? item.test_name && item.b2b_price
                        : item.package_name && item.b2b_price)
                );

                if (formattedData.length === 0) {
                    message.error("No valid rows found in the file!");
                    setLoading(false);
                    return;
                }

                // Call correct API
                const response =
                    uploadType === "test"
                        ? await create_bulk_b2b_tests(formattedData, b2bClient)
                        : await create_bulk_b2b_packages(formattedData, b2bClient);

                if (response?.status !== 201 && response?.status !== 200) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const { created, errors, packages, tests } = response.data;

                if (created?.length) {
                    message.success(
                        `${created.length} ${uploadType}s uploaded successfully!`
                    );
                    setIsBulkUploadModalOpen(false);
                    setFileList([]);
                    setB2bClient(null);
                    setUploadType("test");
                    if (onSuccess) onSuccess();
                }

                if (errors?.length) {
                    const errorItems = errors
                        .map((err) =>
                            uploadType === "test" ? err.test_name : err.package_name
                        )
                        .join(", ");
                    message.warning(
                        `Some ${uploadType}s could not be uploaded: ${errorItems}`
                    );
                }

                if (packages || tests) {
                    const errData = uploadType === "package" ? packages : tests;
                    if (errData) {
                        const errorMessages = Object.entries(errData)
                            .map(([rowIndex, rowErrors]) => {
                                return `Row ${parseInt(rowIndex) + 1}: ${Object.entries(
                                    rowErrors
                                )
                                    .map(([field, msgs]) => `${field} → ${msgs.join(", ")}`)
                                    .join("; ")}`;
                            })
                            .join("\n");

                        message.error(`Upload failed:\n${errorMessages}`);
                    }
                }
            };

            reader.readAsArrayBuffer(file);
        } catch (error) {
            console.log(error);
            message.error(error?.response?.data?.error || "Error uploading file.");
        } finally {
            setLoading(false);
        }
    };


    return (
        <Modal
            title="Bulk Upload B2B Services"
            open={isBulkUploadModalOpen}
            onCancel={() => {
                setIsBulkUploadModalOpen(false);
                setFileList([]);      
                setB2bClient(null);   
                setUploadType("test"); 
            }}
            footer={null}
            width={600}
        >
            <Form>
                {/* Upload Type (Test or Package) */}
                <div className="mb-[1rem] mt-[1rem] w-full">
                    <Select
                        value={uploadType}
                        style={{ width: "100%" }}
                        onChange={(value) => setUploadType(value)}
                    >
                        <Select.Option value="test">Upload Tests</Select.Option>
                        <Select.Option value="package">Upload Packages</Select.Option>
                    </Select>
                </div>

                {/* Select B2B Client */}
                <div className="mb-[1rem] w-full">
                    <Select
                        placeholder="Select B2B Client"
                        style={{ width: "100%" }}
                        onChange={(value) => setB2bClient(value)}
                    >
                        {B2BClients?.map((client) => (
                            <Select.Option key={client.id} value={client.id}>
                                {client.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                {/* File Upload */}
                <Dragger {...props} fileList={fileList}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        Click or drag Excel file to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                        File must contain columns:{" "}
                        <b>
                            {uploadType === "test"
                                ? "test_name, b2b_amount"
                                : "package_name, b2b_amount"}
                        </b>
                    </p>
                </Dragger>

                {/* Action Buttons */}
                <div className="flex justify-end mt-[1rem]">
                    <Tooltip
                        title={
                            !b2bClient
                                ? "Select B2B Client"
                                : !fileList.length
                                    ? "Upload file"
                                    : ""
                        }
                        placement="top"
                    >
                        <Button
                            className="mr-[1rem]"
                            type="primary"
                            onClick={handleUpload}
                            disabled={!fileList.length || !b2bClient}
                            loading={loading}
                        >
                            Upload
                        </Button>
                    </Tooltip>

                    <Button
                        type="primary"
                        onClick={() => {
                            const fileUrl = `${process.env.PUBLIC_URL}/b2b_${uploadType}_sample.xlsx`;
                            const link = document.createElement("a");
                            link.href = fileUrl;
                            link.download = `b2b_${uploadType}_sample.xlsx`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                    >
                        Download Sample
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default BulkUploadB2BTest;
